/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createLogPrefix, logDebug, noop } from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import { cblSignIn, type CblSignInDeps } from '@amzn/vinyl-amzn/cbl'
import { AuthStatus } from '@/auth/auth'
import { localSignInPersistence } from '@/core/signInPersistence'
import { TokenAuth } from '@/auth/TokenAuth'

export interface CblAuthDeps extends Omit<CblSignInDeps, 'codeData'> {
    /**
     * Resolves the code data at sign-in time. Its source (`getDeviceInfo`) is
     * only populated after `initializeDeviceInfo`, so it must not be forced at
     * construction — the controller is built during the boot import graph,
     * before that runs.
     */
    readonly codeDataProvider: () => CblSignInDeps['codeData']
}

/**
 * Manages authentication state with code-based linking.
 * Sets the auth token and DMLS options on the player.
 */
export class CblAuth extends TokenAuth {
    logPrefix = createLogPrefix('CblAuth')

    constructor(private readonly deps: CblAuthDeps) {
        super(AuthStatus.SIGNED_OUT)
    }

    /**
     * Prompts for sign-in (may use local storage to retrieve previous auth).
     */
    signIn() {
        logDebug(this, 'signIn')
        if (this._status.value !== AuthStatus.SIGNED_OUT) return
        this.setStatus(AuthStatus.PENDING)
        const { codeDataProvider, ...signInDeps } = this.deps
        ;(async () => {
            this._authTokenProvider = await cblSignIn({
                ...signInDeps,
                codeData: codeDataProvider(),
            })
            logDebug(this, 'signInComplete')
            this.setStatus(AuthStatus.SIGNED_IN)
        })().catch(createErrorHandler(this))
    }

    /**
     * Signs out, clearing the currently authenticated user.
     * Emits a 'signOut' event.
     */
    signOut() {
        logDebug(this, 'signOut')
        localSignInPersistence.remove().catch(noop)
        this._authTokenProvider = null
        this.setStatus(AuthStatus.SIGNED_OUT)
    }
}
